import {
  createContext,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  type RefCallback,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Key, Selection, SortDescriptor, TableLayoutProps, TableProps } from 'react-aria-components';
import { Cell, Column, ResizableTableContainer, Row, Table, TableBody, TableHeader } from 'react-aria-components';
import { enableDatatableDefaultColumnActions } from '@gonfalon/dogfood-flags/dogfood-flags';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { useLatestRef } from '@gonfalon/react/useLatestRef';
import { TableEmptyState } from '@gonfalon/ui/Table';
import { Checkbox, ProgressBar, Virtualizer } from '@launchpad-ui/components';
import tokens from '@launchpad-ui/tokens';
import clsx from 'clsx';

import { ColumnManager } from './ColumnManager';
import { DataTableCellActions } from './DataTableCellActions';
import { DataTableCellButtons } from './DataTableCellButtons';
import { DataTableColumnHeader } from './DataTableColumnHeader';
import { DataTableTreeGutter } from './DataTableTreeGutter';
import { flattenRows } from './flattenRows';
import { BodyItemCache, type DetailBodyItem, type RowBodyItem } from './internal/bodyItemCache';
import {
  decodeColumnKeys,
  decodeColumnWidths,
  decodeSortDescriptor,
  encodeColumnInput,
  encodeSortDescriptor,
} from './internal/columnKeys';
import {
  layoutVisibleColumns,
  orderAfterVisualMove,
  widthsForDisplayedColumns,
  withRowHeaderFallback,
} from './internal/columnLayout';
import { DataTableAdditionalResultsRow, DataTableHeaderBannerRow } from './internal/DataTableAdditionalResults';
import { DataTableLayout, type DataTableLayoutOptions } from './internal/DataTableLayout';
import {
  DataTableOpenCellActionsContext,
  DataTableRowActions,
  type OpenCellActions,
} from './internal/DataTableRowActions';
import { getDefaultCellActions } from './internal/defaultCellActions';
import { foldedLinesForNarrow } from './internal/narrowColumns';
import { useColumnPromotion } from './internal/useColumnPromotion';
import { useDataTableNarrow } from './internal/useDataTableNarrow';
import { useScrollAnchorTarget } from './internal/useScrollAnchorTarget';
import { useScrollEdges } from './internal/useScrollEdges';
import { useScrollToRow } from './internal/useScrollToRow';
import { sortRows } from './sortRows';
import type {
  ColumnWidth,
  DataTableAction,
  DataTableAdditionalResults,
  DataTableAttributeColumns,
  DataTableCellFilter,
  DataTableColumn,
  DataTableColumnPromotion,
  DataTableColumnRef,
  DataTableColumnRefInput,
  DataTableColumnWidth,
  DataTableContainerStyle,
  DataTableHorizontalPadding,
  DataTableInlineEdge,
  DataTableNarrowColumns,
  DataTableSize,
  DataTableSortDescriptor,
  DataTableSortDescriptorInput,
} from './types';
import { useColumnReordering } from './useColumnReordering';
import { useControllableState } from './useControllableState';
import { type ResolvedColumn, useDataTableColumns } from './useDataTableColumns';
import { useInfiniteScroll } from './useInfiniteScroll';
import {
  stickyEndExtent,
  stickyEndOffset,
  stickyStartExtent,
  stickyStartOffset,
  useStickyColumnOffsets,
} from './useStickyColumnOffsets';

import styles from './DataTable.module.css';

const SELECTION_COLUMN_ID = 'lp-data-table-selection';
const FUNCTION_COLUMN_ID = 'lp-data-table-functions';
const UTILITY_CELL_PADDING_INLINE = 4;
const CHECKBOX_SIZE = 16;
const SELECTION_CELL_PADDING_START = 12;
const FUNCTION_COLUMN_WIDTH = 20 + 2 * UTILITY_CELL_PADDING_INLINE;
const ROOT_FONT_SIZE = 16;

const horizontalPaddingWidth = (padding: DataTableHorizontalPadding | undefined) => {
  if (padding === undefined) {
    return 0;
  }
  const value = tokens.spacing[padding];
  return typeof value === 'number' ? value : Number.parseFloat(value) * ROOT_FONT_SIZE;
};

const horizontalPaddingProperty = (padding: DataTableHorizontalPadding) =>
  horizontalPaddingWidth(padding) === 0 ? '0px' : `var(--lp-spacing-${padding})`;

const FEED_RESULTS_ROW_ID = 'lp-data-table-additional-feed-results';
const EMPTY_ROW_ID = 'lp-data-table-empty';
const EMPTY_FOLDED_COLUMNS: readonly string[] = [];
const EMPTY_FOLDED_REFS: readonly DataTableColumnRef[] = [];

const DataTableFoldedColumnsContext = createContext<readonly DataTableColumnRef[]>(EMPTY_FOLDED_REFS);

export function useDataTableFoldedColumns() {
  return useContext(DataTableFoldedColumnsContext);
}

const isInteractiveTarget = (target: EventTarget) =>
  target instanceof Element && target.closest('button, a, input, select, textarea, [role="button"]') !== null;

const blurFocusWithin = (element: HTMLElement) => {
  const active = document.activeElement;
  if (active instanceof HTMLElement && element.contains(active)) {
    active.blur();
  }
};

const currentRowRef =
  (forwardedRef: RefCallback<HTMLElement> | undefined): RefCallback<HTMLElement> =>
  (element) => {
    forwardedRef?.(element);
    element?.setAttribute('aria-current', 'true');
    return () => {
      element?.removeAttribute('aria-current');
      forwardedRef?.(null);
    };
  };

const isBlank = (value: ReactNode) => value === null || value === undefined || value === '';
const getPrimitiveLabel = (value: ReactNode, fallback: Key) =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : String(fallback);

const SCROLL_TIMELINE_NAME = '--lp-data-table-x';
const PAN_LOCK_PX = 4;

const wheelLineHeight = (element: HTMLElement) => {
  const style = getComputedStyle(element);
  const lineHeight = Number.parseFloat(style.lineHeight);
  if (Number.isFinite(lineHeight) && lineHeight > 0) {
    return lineHeight;
  }
  const fontSize = Number.parseFloat(style.fontSize);
  return Number.isFinite(fontSize) && fontSize > 0 ? fontSize : 16;
};

const horizontalWheelDelta = (event: WheelEvent, element: HTMLElement, pageSize: number) => {
  const shifted = event.shiftKey && Math.abs(event.deltaY) > Math.abs(event.deltaX);
  if (!shifted && Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
    return 0;
  }
  let delta = shifted ? event.deltaY : event.deltaX;
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
    delta *= wheelLineHeight(element);
  } else if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    delta *= pageSize;
  }
  return delta;
};

const supportsScrollTimeline = () => {
  if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') {
    return false;
  }

  return (
    CSS.supports('animation-timeline', SCROLL_TIMELINE_NAME) &&
    CSS.supports('scroll-timeline', `${SCROLL_TIMELINE_NAME} inline`) &&
    CSS.supports('timeline-scope', SCROLL_TIMELINE_NAME) &&
    CSS.supports('animation-duration', 'auto')
  );
};

type FeedBodyItem = { type: 'feed'; id: typeof FEED_RESULTS_ROW_ID; content: ReactNode };
type EmptyBodyItem = { type: 'empty'; id: typeof EMPTY_ROW_ID; render: (() => ReactNode) | undefined };
type BodyItem<T extends object> = RowBodyItem<T> | DetailBodyItem<T> | FeedBodyItem | EmptyBodyItem;

export type DataTableVirtualizationOptions = Pick<
  TableLayoutProps,
  'rowHeight' | 'estimatedRowHeight' | 'headingHeight' | 'estimatedHeadingHeight'
>;

type DataTableViewportProps =
  | {
      height?: number | string;
      virtualization?: never;
      scrollContainer?: 'self';
    }
  | {
      virtualization: DataTableVirtualizationOptions;
      height: number | string;
      scrollContainer?: 'self';
    }
  | {
      scrollContainer: 'ancestor';
      height?: never;
      virtualization?: DataTableVirtualizationOptions;
    };

export type DataTableProps<T extends object> = DataTableViewportProps & {
  'aria-label': string;
  columns: Array<DataTableColumn<T>>;
  rows: T[];
  getRowId: (row: T) => Key;

  size?: DataTableSize;
  bordered?: boolean;
  hasHeaderTopBorder?: boolean;
  hasSelectedRowHighlight?: boolean;
  horizontalPadding?: DataTableHorizontalPadding;

  isHeaderSticky?: boolean;

  stickyHeaderOffset?: number;

  sortMode?: 'client' | 'server';
  sortDescriptor?: DataTableSortDescriptorInput;
  defaultSortDescriptor?: DataTableSortDescriptorInput;
  onSortChange?: (descriptor?: DataTableSortDescriptor) => void;
  disallowEmptySort?: boolean;

  getChildRows?: (row: T) => T[] | undefined;

  getRowLabel?: (row: T) => string;
  expandedKeys?: Key[];
  defaultExpandedKeys?: Key[];
  onExpandedKeysChange?: (keys: Key[]) => void;

  renderExpandedContent?: (row: T) => ReactNode;
  isRowExpandable?: (row: T) => boolean;

  hiddenColumns?: DataTableColumnRefInput[];
  defaultHiddenColumns?: DataTableColumnRefInput[];
  onHiddenColumnsChange?: (hidden: DataTableColumnRef[]) => void;

  columnOrder?: DataTableColumnRefInput[];
  defaultColumnOrder?: DataTableColumnRefInput[];
  onColumnOrderChange?: (order: DataTableColumnRef[]) => void;

  isColumnResizable?: boolean;
  onColumnResize?: (widths: DataTableColumnWidth[]) => void;
  onColumnResizeEnd?: (widths: DataTableColumnWidth[]) => void;
  onColumnResetSize?: (column: DataTableColumnRef) => void;

  stickyColumnCount?: number;
  pinFunctionColumn?: boolean;

  columnPromotion?: DataTableColumnPromotion;

  narrowColumns?: DataTableNarrowColumns;

  attributeColumns?: DataTableAttributeColumns<T>;
  attributeColumnKeys?: string[];
  defaultAttributeColumnKeys?: string[];
  onAttributeColumnKeysChange?: (keys: string[]) => void;

  hasColumnManager?: boolean;

  getRowActions?: (row: T) => Array<DataTableAction<T>>;

  isLoading?: boolean;

  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;

  loadMoreThreshold?: number;

  additionalResults?: DataTableAdditionalResults;

  selectionMode?: TableProps['selectionMode'];
  selectedKeys?: Selection;
  defaultSelectedKeys?: Selection;
  onSelectionChange?: (keys: Selection) => void;

  onRowAction?: (row: T) => void;

  getRowHref?: (row: T) => string | undefined;

  scrollToRowId?: Key;

  activeRowId?: Key;

  onCellFilter?: (filter: DataTableCellFilter<T>) => void;

  onRowHover?: (row: T | null) => void;

  additionalFeedResults?: ReactNode;

  emptyState?: { title: string; description: string };
  renderEmptyState?: () => ReactNode;
};

const NO_KEYS: Key[] = [];
const renderLoadingState = () => (
  <div className={styles.loadingState}>
    <ProgressBar aria-label={msg('ui.dataTable.loading.label')} isIndeterminate />
  </div>
);

type ColumnSort = { column: string; direction: DataTableSortDescriptor['direction'] };

type CellActionHandlers<T> = {
  getDisplayedText: () => string | undefined;
  onCellFilter: ((filter: DataTableCellFilter<T>) => void) | undefined;
};

const noDisplayedText = () => undefined;
const ignoreCellFilter = () => {};

export function DataTable<T extends object>(dataTableProps: DataTableProps<T>) {
  const isSortControlled = Object.hasOwn(dataTableProps, 'sortDescriptor');
  const {
    columns,
    rows,
    getRowId,
    size = 'default',
    bordered,
    hasHeaderTopBorder,
    hasSelectedRowHighlight = true,
    horizontalPadding,
    isHeaderSticky = true,
    stickyHeaderOffset,
    height,
    virtualization: virtualizationOptions,
    scrollContainer = 'self',
    sortMode = 'client',
    sortDescriptor: sortDescriptorProp,
    defaultSortDescriptor,
    onSortChange,
    disallowEmptySort = false,
    getChildRows,
    getRowLabel,
    expandedKeys,
    defaultExpandedKeys = NO_KEYS,
    onExpandedKeysChange,
    renderExpandedContent,
    isRowExpandable,
    hiddenColumns,
    defaultHiddenColumns,
    onHiddenColumnsChange,
    columnOrder,
    defaultColumnOrder,
    onColumnOrderChange,
    isColumnResizable = true,
    onColumnResize,
    onColumnResizeEnd,
    onColumnResetSize,
    stickyColumnCount = 0,
    pinFunctionColumn = false,
    columnPromotion,
    narrowColumns,
    attributeColumns,
    attributeColumnKeys,
    defaultAttributeColumnKeys,
    onAttributeColumnKeysChange,
    hasColumnManager = true,
    getRowActions,
    isLoading = false,
    onLoadMore,
    hasMore = false,
    isLoadingMore = false,
    loadMoreThreshold = 200,
    additionalResults,
    selectionMode,
    selectedKeys,
    defaultSelectedKeys,
    onSelectionChange,
    onRowAction,
    getRowHref,
    scrollToRowId,
    activeRowId,
    onCellFilter,
    onRowHover,
    additionalFeedResults,
    emptyState,
    renderEmptyState,
    ...props
  } = dataTableProps;
  const virtualization = scrollContainer === 'ancestor' && rows.length === 0 ? undefined : virtualizationOptions;
  const isVirtualized = virtualization !== undefined;

  const defaultColumnActionsEnabled = enableDatatableDefaultColumnActions();
  const [headerElement, setHeaderElement] = useState<HTMLElement | null>(null);
  const [additionalResultsHeight, setAdditionalResultsHeight] = useState(0);
  const [renderedHeaderHeight, setRenderedHeaderHeight] = useState(0);
  const reportAdditionalResultsHeight = useCallback((nextBannerHeight: number, nextHeaderHeight: number) => {
    setAdditionalResultsHeight((current) => (current === nextBannerHeight ? current : nextBannerHeight));
    setRenderedHeaderHeight((current) => (current === nextHeaderHeight ? current : nextHeaderHeight));
  }, []);
  const hasAdditionalFeedResults =
    additionalFeedResults !== undefined && additionalFeedResults !== null && additionalFeedResults !== false;
  const feedResultsInHeader = virtualization !== undefined && hasAdditionalFeedResults && !additionalResults;
  const hasHeaderBanner = Boolean(additionalResults) || feedResultsInHeader;
  const bannerHeight = hasHeaderBanner ? additionalResultsHeight : 0;
  const measuredHeaderHeight = hasHeaderBanner ? renderedHeaderHeight : 0;
  const headerRef = useCallback((node: HTMLDivElement | HTMLTableSectionElement | null) => {
    setHeaderElement(node);
  }, []);
  const [uncontrolledSortDescriptor, setUncontrolledSortDescriptor] = useState(() =>
    encodeSortDescriptor(defaultSortDescriptor),
  );
  const controlledSortDescriptor = useMemo(() => encodeSortDescriptor(sortDescriptorProp), [sortDescriptorProp]);
  const sortDescriptor = isSortControlled ? controlledSortDescriptor : uncontrolledSortDescriptor;

  const appliedSortRef = useRef<ColumnSort | undefined>(undefined);
  const setSortDescriptor = useCallback(
    (nextDescriptor?: ColumnSort) => {
      if (!isSortControlled) {
        setUncontrolledSortDescriptor(nextDescriptor);
      }
      onSortChange?.(decodeSortDescriptor(nextDescriptor));
    },
    [isSortControlled, onSortChange],
  );

  const [expanded, setExpanded] = useControllableState(expandedKeys, defaultExpandedKeys, onExpandedKeysChange);

  const {
    orderedColumns,
    visibleColumns,
    hiddenColumns: hiddenColumnIds,
    attributeColumnKeys: selectedAttributeKeys,
    toggleColumnVisibility,
    canMoveColumnRelative,
    moveColumnRelative,
    applyColumnOrder,
    addAttributeColumn,
    removeAttributeColumn,
  } = useDataTableColumns({
    columns,
    attributeColumns,
    columnOrder,
    defaultColumnOrder,
    onColumnOrderChange,
    hiddenColumns,
    defaultHiddenColumns,
    onHiddenColumnsChange,
    attributeColumnKeys,
    defaultAttributeColumnKeys,
    onAttributeColumnKeysChange,
  });

  const [resizedWidths, setResizedWidths] = useState<Map<Key, ColumnWidth>>();
  const [controlsResizedWidths, setControlsResizedWidths] = useState(false);
  const emitColumnResize = useCallback(
    (widths: Map<Key, ColumnWidth>) => {
      onColumnResize?.(decodeColumnWidths(widths));
    },
    [onColumnResize],
  );
  const handleColumnResize = useCallback(
    (widths: Map<Key, ColumnWidth>) => {
      if (controlsResizedWidths) {
        setResizedWidths(widths);
      }
      emitColumnResize(widths);
    },
    [controlsResizedWidths, emitColumnResize],
  );
  const handleColumnResizeEnd = useCallback(
    (widths: Map<Key, ColumnWidth>) => {
      if (defaultColumnActionsEnabled) {
        setResizedWidths(widths);
      }
      onColumnResizeEnd?.(decodeColumnWidths(widths));
    },
    [defaultColumnActionsEnabled, onColumnResizeEnd],
  );
  const resetColumnSize = (column: ResolvedColumn<T>) => {
    if (!defaultColumnActionsEnabled) {
      return;
    }
    const next = new Map(resizedWidths);
    next.delete(column.id);
    setResizedWidths(next);
    setControlsResizedWidths(true);
    onColumnResetSize?.(column.ref);
  };
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);
  const [ancestorXScroll, setAncestorXScroll] = useState<HTMLDivElement | null>(null);
  const [ancestorXBar, setAncestorXBar] = useState<HTMLDivElement | null>(null);
  const [ancestorXContentWidth, setAncestorXContentWidth] = useState(0);
  const [ancestorXBarHidden, setAncestorXBarHidden] = useState(true);
  const [tableElement, setTableElement] = useState<HTMLDivElement | HTMLTableElement | null>(null);
  const [headerRowHeight, setHeaderRowHeight] = useState(0);
  const [openCellActions, setOpenCellActions] = useState<OpenCellActions>();
  const openCellActionsState = useMemo(() => ({ openCellActions, setOpenCellActions }), [openCellActions]);
  const cellActionsAnchorRef = useRef<Element | null>(null);
  const shouldRestoreCellFocusRef = useRef(false);
  useEffect(() => {
    const anchor = cellActionsAnchorRef.current;
    if (openCellActions === undefined && shouldRestoreCellFocusRef.current && anchor instanceof HTMLElement) {
      shouldRestoreCellFocusRef.current = false;
      anchor.focus();
    }
  }, [openCellActions]);

  useLayoutEffect(() => {
    const row = headerElement?.firstElementChild;
    if (!(row instanceof HTMLElement)) {
      return;
    }

    const update = () => {
      setHeaderRowHeight(row.getBoundingClientRect().height);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(row);
    return () => observer.disconnect();
  }, [headerElement]);

  useLayoutEffect(() => {
    if (scrollContainer !== 'ancestor' || !ancestorXScroll) {
      return;
    }

    const update = () => {
      const width = tableElement?.scrollWidth ?? 0;
      const overflows = width > ancestorXScroll.clientWidth + 1;
      setAncestorXContentWidth(width);
      setAncestorXBarHidden(!overflows);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(ancestorXScroll);
    if (tableElement) {
      observer.observe(tableElement);
      if (isVirtualized && tableElement.firstElementChild) {
        observer.observe(tableElement.firstElementChild);
      }
    }
    return () => observer.disconnect();
  }, [ancestorXScroll, isVirtualized, scrollContainer, tableElement]);

  useLayoutEffect(() => {
    if (scrollContainer !== 'ancestor' || !ancestorXBar || !ancestorXScroll || !containerElement) {
      return;
    }

    const usesFallback = !supportsScrollTimeline();
    containerElement.toggleAttribute('data-x-scroll-fallback', usesFallback);

    const update = () => {
      const scrollWidth = ancestorXBarHidden ? 0 : Math.max(ancestorXBar.scrollWidth, ancestorXContentWidth);
      const maxScroll = Math.max(0, scrollWidth - ancestorXBar.clientWidth);
      containerElement.style.setProperty('--lp-data-table-x-shift', `${-maxScroll}px`);
      if (usesFallback) {
        containerElement.style.setProperty('--lp-data-table-x-scroll', `${ancestorXBar.scrollLeft}px`);
      }
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(ancestorXBar);
    observer.observe(ancestorXScroll);
    if (usesFallback) {
      ancestorXBar.addEventListener('scroll', update, { passive: true });
    }
    return () => {
      observer.disconnect();
      ancestorXBar.removeEventListener('scroll', update);
      containerElement.removeAttribute('data-x-scroll-fallback');
      containerElement.style.removeProperty('--lp-data-table-x-shift');
      containerElement.style.removeProperty('--lp-data-table-x-scroll');
    };
  }, [ancestorXBar, ancestorXBarHidden, ancestorXContentWidth, ancestorXScroll, containerElement, scrollContainer]);

  useLayoutEffect(() => {
    if (scrollContainer !== 'ancestor' || !ancestorXScroll || !ancestorXBar) {
      return;
    }

    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || ancestorXBar.hidden) {
        return;
      }
      const delta = horizontalWheelDelta(event, ancestorXScroll, ancestorXBar.clientWidth);
      if (delta === 0) {
        return;
      }
      const previous = ancestorXBar.scrollLeft;
      ancestorXBar.scrollLeft += delta;
      if (ancestorXBar.scrollLeft !== previous) {
        event.preventDefault();
      }
    };

    let pan: { id: number; x: number; y: number; scroll: number; axis?: 'x' | 'y' } | undefined;
    const onTouchStart = (event: TouchEvent) => {
      pan = undefined;
      if (event.touches.length !== 1 || ancestorXBar.hidden) {
        return;
      }
      const touch = event.touches[0];
      if (
        touch.target instanceof Element &&
        touch.target.closest('[role="columnheader"], button, a, input, [data-column-resizer]')
      ) {
        return;
      }
      pan = { id: touch.identifier, x: touch.clientX, y: touch.clientY, scroll: ancestorXBar.scrollLeft };
    };
    const onTouchMove = (event: TouchEvent) => {
      if (!pan || event.touches.length !== 1) {
        return;
      }
      const touch = event.touches[0];
      if (touch.identifier !== pan.id) {
        return;
      }
      const dx = touch.clientX - pan.x;
      const dy = touch.clientY - pan.y;
      if (pan.axis === undefined) {
        if (Math.hypot(dx, dy) < PAN_LOCK_PX) {
          return;
        }
        pan.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (pan.axis !== 'x') {
        return;
      }
      ancestorXBar.scrollLeft = pan.scroll - dx;
    };
    const endPan = () => {
      pan = undefined;
    };

    ancestorXScroll.addEventListener('wheel', onWheel, { passive: false });
    ancestorXScroll.addEventListener('touchstart', onTouchStart, { passive: true });
    ancestorXScroll.addEventListener('touchmove', onTouchMove, { passive: true });
    ancestorXScroll.addEventListener('touchend', endPan);
    ancestorXScroll.addEventListener('touchcancel', endPan);
    return () => {
      ancestorXScroll.removeEventListener('wheel', onWheel);
      ancestorXScroll.removeEventListener('touchstart', onTouchStart);
      ancestorXScroll.removeEventListener('touchmove', onTouchMove);
      ancestorXScroll.removeEventListener('touchend', endPan);
      ancestorXScroll.removeEventListener('touchcancel', endPan);
    };
  }, [ancestorXBar, ancestorXScroll, scrollContainer]);

  const openActionsFromCell = useCallback(
    (rowId: Key, columnId: string) => (event: MouseEvent) => {
      if (
        !(event.target instanceof Node) ||
        !event.currentTarget.contains(event.target) ||
        isInteractiveTarget(event.target)
      ) {
        return;
      }
      event.stopPropagation();
      cellActionsAnchorRef.current = event.currentTarget;
      setOpenCellActions({ rowId, columnId, anchor: event.currentTarget });
    },
    [],
  );
  const openActionsFromFocusedCell = useCallback((event: KeyboardEvent) => {
    if (
      (event.key === 'Enter' || event.key === ' ') &&
      event.target instanceof HTMLElement &&
      event.target.hasAttribute('data-cell-actions')
    ) {
      event.preventDefault();
      event.stopPropagation();
      event.target.click();
    }
  }, []);
  const columnReordering = useColumnReordering({
    container: containerElement,
    canMoveColumn: canMoveColumnRelative,
    moveColumn: moveColumnRelative,
  });
  const expandedSet = useMemo(() => new Set(expanded), [expanded]);
  const narrow = useDataTableNarrow(narrowColumns ? containerElement : null);
  const encodedNarrowColumns = useMemo(
    () =>
      narrowColumns
        ? {
            mainColumnId:
              narrowColumns.mainColumnId === undefined ? undefined : encodeColumnInput(narrowColumns.mainColumnId),
            priority: narrowColumns.priority.map((entry) =>
              Array.isArray(entry) ? entry.map(encodeColumnInput) : encodeColumnInput(entry),
            ),
          }
        : undefined,
    [narrowColumns],
  );
  const encodedPromotion = useMemo(
    () =>
      columnPromotion
        ? {
            ...columnPromotion,
            nameColumnId: encodeColumnInput(columnPromotion.nameColumnId),
            reserved: columnPromotion.reserved.map((column) => ({ ...column, id: encodeColumnInput(column.id) })),
            candidates: columnPromotion.candidates.map((column) => ({ ...column, id: encodeColumnInput(column.id) })),
          }
        : undefined,
    [columnPromotion],
  );
  const hasSelectionColumn = selectionMode === 'single' || selectionMode === 'multiple';
  const hasRowActivation = onRowAction !== undefined || getRowHref !== undefined;
  const hasFunctionColumn = hasColumnManager || getRowActions !== undefined;
  const utilitySpacerWidth = Math.max(0, horizontalPaddingWidth(horizontalPadding) - UTILITY_CELL_PADDING_INLINE);
  const selectionColumnWidth =
    (horizontalPadding === undefined
      ? SELECTION_CELL_PADDING_START
      : UTILITY_CELL_PADDING_INLINE + utilitySpacerWidth) +
    CHECKBOX_SIZE +
    UTILITY_CELL_PADDING_INLINE;
  const functionColumnWidth = FUNCTION_COLUMN_WIDTH + utilitySpacerWidth;
  const utilityEdge = (hasUtilityColumn: boolean, edge: DataTableInlineEdge) =>
    horizontalPadding !== undefined && hasUtilityColumn ? edge : undefined;
  const selectionEdge = utilityEdge(hasSelectionColumn, 'start');
  const functionEdge = utilityEdge(hasFunctionColumn, 'end');
  const isTree = getChildRows !== undefined;
  const visibleIds = useMemo(() => new Set(visibleColumns.map((column) => column.id)), [visibleColumns]);
  const promotionConfig = useMemo(() => {
    if (!encodedPromotion || !visibleIds.has(encodedPromotion.nameColumnId)) {
      return undefined;
    }
    return {
      nameColumnId: encodedPromotion.nameColumnId,
      nameMinWidth: encodedPromotion.nameMinWidth,
      chromeWidth:
        encodedPromotion.chromeWidth +
        (selectionEdge ? utilitySpacerWidth : 0) +
        (functionEdge ? utilitySpacerWidth : 0),
      reserved: encodedPromotion.reserved.filter((column) => visibleIds.has(column.id)),
      candidates: encodedPromotion.candidates.filter((column) => visibleIds.has(column.id)),
    };
  }, [encodedPromotion, functionEdge, selectionEdge, utilitySpacerWidth, visibleIds]);
  const promotion = useColumnPromotion(containerElement, promotionConfig);
  const foldedLines = useMemo(
    () => (narrow && encodedNarrowColumns ? foldedLinesForNarrow(visibleColumns, encodedNarrowColumns) : []),
    [narrow, encodedNarrowColumns, visibleColumns],
  );
  const columnsById = useMemo(() => new Map(visibleColumns.map((column) => [column.id, column])), [visibleColumns]);
  const narrowFoldedIds = useMemo(() => foldedLines.flat(), [foldedLines]);
  const openCellAnchor = openCellActions?.anchor;
  const openCellTriggerRef = useMemo(() => ({ current: openCellAnchor ?? null }), [openCellAnchor]);
  const readOpenCellText = useCallback(() => {
    const text = openCellAnchor?.querySelector('[data-cell-primary]')?.textContent;
    return text?.replace(/\s+/g, ' ').trim() || undefined;
  }, [openCellAnchor]);
  const hasCellFilter = onCellFilter !== undefined;
  const hasRowHover = onRowHover !== undefined;
  const onRowHoverRef = useLatestRef(onRowHover);
  const resolveCellActions = useCallback(
    (
      column: ResolvedColumn<T>,
      row: T,
      content: ReactNode,
      isFoldedStack: boolean,
      handlers: CellActionHandlers<T>,
    ): Array<DataTableAction<T>> => {
      const defaults =
        column.hasDefaultCellActions === false ||
        isFoldedStack ||
        (column.isRowHeader && hasRowActivation) ||
        column.onCellAction !== undefined ||
        (column.getCellButtons?.(row).length ?? 0) > 0
          ? []
          : getDefaultCellActions(column, row, { content, ...handlers });
      return column.getCellActions ? column.getCellActions(row, defaults) : defaults;
    },
    [hasRowActivation],
  );
  const rowCellActionHandlers = useMemo<CellActionHandlers<T>>(
    () => ({ getDisplayedText: noDisplayedText, onCellFilter: hasCellFilter ? ignoreCellFilter : undefined }),
    [hasCellFilter],
  );
  const foldedColumnIds = narrow && narrowColumns ? narrowFoldedIds : (promotion?.foldedIds ?? EMPTY_FOLDED_COLUMNS);
  const foldedSet = useMemo(() => new Set(foldedColumnIds), [foldedColumnIds]);
  const foldedColumnRefs = useMemo(
    () => (foldedColumnIds.length === 0 ? EMPTY_FOLDED_REFS : decodeColumnKeys(foldedColumnIds)),
    [foldedColumnIds],
  );
  const unfoldedColumns = useMemo(
    () => visibleColumns.filter((column) => !foldedSet.has(column.id)),
    [foldedSet, visibleColumns],
  );
  const pinnedLayout = useMemo(() => layoutVisibleColumns(unfoldedColumns), [unfoldedColumns]);
  const displayColumns = useMemo(() => withRowHeaderFallback(pinnedLayout.columns), [pinnedLayout]);
  const columnWidths = useMemo(() => {
    if (!promotion) {
      return undefined;
    }
    if (!narrow || !narrowColumns) {
      return promotion.widths;
    }
    const displayedIds = displayColumns.map((column) => column.id);
    const absorbId =
      encodedPromotion && displayedIds.includes(encodedPromotion.nameColumnId)
        ? encodedPromotion.nameColumnId
        : displayedIds[0];
    return widthsForDisplayedColumns(promotion.widths, displayedIds, absorbId);
  }, [encodedPromotion, displayColumns, narrow, narrowColumns, promotion]);
  const stickyDataColumnCount = pinnedLayout.usesIdentityPin
    ? displayColumns.filter((column) => column.pin === 'start').length
    : Number.isFinite(stickyColumnCount)
      ? Math.min(Math.max(Math.floor(stickyColumnCount), 0), displayColumns.length)
      : 0;
  const endPinnedCount = displayColumns.filter((column) => column.pin === 'end').length;
  const hasStickyColumns = stickyDataColumnCount > 0;
  const pinFunction = pinFunctionColumn && hasFunctionColumn;
  const endStickyCount = endPinnedCount + (pinFunction ? 1 : 0);
  const lastStickyColumnIndex = stickyDataColumnCount - 1;
  const stickyColumnKey = [
    hasSelectionColumn && hasStickyColumns ? SELECTION_COLUMN_ID : '',
    ...displayColumns.slice(0, stickyDataColumnCount).map((column) => column.id),
    ...displayColumns.filter((column) => column.pin === 'end').map((column) => column.id),
    pinFunction ? FUNCTION_COLUMN_ID : '',
  ].join(':');
  const stickyExtents = useStickyColumnOffsets(tableElement, containerElement, stickyColumnKey);

  useLayoutEffect(() => {
    if (scrollContainer !== 'ancestor' || !ancestorXScroll || !ancestorXBar) {
      return;
    }

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof Element) || ancestorXBar.hidden) {
        return;
      }
      const cell = target.closest('th, td, [role="columnheader"], [role="gridcell"], [role="rowheader"]');
      if (!(cell instanceof HTMLElement)) {
        return;
      }
      if (cell.hasAttribute('data-sticky-column-index') || cell.hasAttribute('data-sticky-end-index')) {
        return;
      }
      const view = ancestorXBar.getBoundingClientRect();
      const box = cell.getBoundingClientRect();
      const visibleLeft = view.left + stickyExtents.current.startExtent;
      const visibleRight = view.right - stickyExtents.current.endExtent;
      if (box.left < visibleLeft) {
        ancestorXBar.scrollLeft -= visibleLeft - box.left;
      } else if (box.right > visibleRight) {
        ancestorXBar.scrollLeft += box.right - visibleRight;
      }
    };

    ancestorXScroll.addEventListener('focusin', onFocusIn);
    return () => {
      ancestorXScroll.removeEventListener('focusin', onFocusIn);
    };
  }, [ancestorXBar, ancestorXScroll, scrollContainer, stickyExtents]);

  const scrollElement =
    scrollContainer === 'ancestor' ? ancestorXBar : virtualization ? tableElement : containerElement;
  const scrollEdges = useScrollEdges(scrollElement);
  const flatRows = useMemo(
    () =>
      flattenRows({
        rows,
        getRowId,
        getChildRows,
        expandedKeys: expandedSet,
        sort: (items) => (sortMode === 'client' ? sortRows(items, orderedColumns, sortDescriptor) : items),
      }),
    [rows, getRowId, getChildRows, expandedSet, sortMode, orderedColumns, sortDescriptor],
  );
  const rowById = useMemo(() => new Map(flatRows.map((flatRow) => [flatRow.id, flatRow.row])), [flatRows]);
  const rowIdByString = useMemo(() => new Map([...rowById.keys()].map((key) => [String(key), key])), [rowById]);
  const handleRowAction = useCallback(
    (key: Key) => {
      const row = rowById.get(key);
      if (row !== undefined && getRowHref?.(row) === undefined) {
        onRowAction?.(row);
      }
    },
    [getRowHref, onRowAction, rowById],
  );
  const handleCellActionKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }
      const { cellActionColumn: columnId, cellActionRow: rowId } = target.dataset;
      if (columnId === undefined || rowId === undefined) {
        return;
      }
      const column = displayColumns.find((candidate) => candidate.id === columnId);
      const onCellAction = column?.onCellAction;
      const key = rowIdByString.get(rowId);
      const row = key === undefined ? undefined : rowById.get(key);
      if (!column || !onCellAction || row === undefined) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (!event.repeat) {
        onCellAction(row, column.ref);
      }
    },
    [displayColumns, rowById, rowIdByString],
  );
  const [selection, setSelection] = useControllableState<Selection>(
    selectedKeys,
    defaultSelectedKeys ?? new Set(),
    onSelectionChange,
  );
  const selectionPressRef = useRef<{ isSelectionControl: boolean; target: Element | null } | null>(null);
  const handleContainerPointerDownCapture = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const target = event.target instanceof Element ? event.target : null;
    selectionPressRef.current = {
      isSelectionControl: Boolean(target?.closest(`.${styles.selectionColumn}`)),
      target,
    };
  }, []);
  const handleSelectionChange = useCallback(
    (keys: Selection) => {
      const press = selectionPressRef.current;
      if (!press || press.isSelectionControl) {
        setSelection(keys);
        return;
      }
      if (
        !onRowAction ||
        press.target?.closest('[data-cell-actions], button, a, input, select, textarea, [role="button"]')
      ) {
        return;
      }
      const rowKey = press.target?.closest('[role="row"]')?.getAttribute('data-key');
      const rowId = rowKey === null || rowKey === undefined ? undefined : rowIdByString.get(rowKey);
      const row = rowId === undefined ? undefined : rowById.get(rowId);
      if (row !== undefined) {
        onRowAction(row);
      }
    },
    [onRowAction, rowById, rowIdByString, setSelection],
  );
  const handleContainerKeyDownCapture = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      selectionPressRef.current = null;
      openActionsFromFocusedCell(event);
      handleCellActionKeyDown(event);
    },
    [openActionsFromFocusedCell, handleCellActionKeyDown],
  );
  const lastFlatRowId = flatRows[flatRows.length - 1]?.id;
  const lastRow = rows.at(-1);
  const lastRowId = lastRow === undefined ? undefined : getRowId(lastRow);
  const [virtualLayout] = useState(() => new DataTableLayout<T>());
  useScrollToRow({
    rowId: scrollToRowId,
    hasRow: scrollToRowId !== undefined && rowById.has(scrollToRowId),
    scrollContainer,
    layout: virtualization ? virtualLayout : undefined,
    containerElement,
    tableElement,
    headerElement,
    stickyHeaderOffset,
  });
  const scrollAnchorTarget = useScrollAnchorTarget({
    isVirtualized: virtualization !== undefined,
    scrollContainer,
    containerElement,
    tableElement,
    stickyHeaderOffset,
  });
  const scrollAnchorContent = useMemo(() => ({ rows, sortDescriptor }), [rows, sortDescriptor]);
  const selfScrollElement = virtualization ? tableElement : containerElement;
  const loadMoreTargetRef = useInfiniteScroll({
    hasMore: hasMore && !isLoading,
    isLoading: isLoadingMore,
    itemCount: rows.length,
    onLoadMore,
    root: scrollContainer === 'ancestor' ? null : selfScrollElement,
    threshold: loadMoreThreshold,
    resetKey: lastRowId,
  });

  const expandedRef = useLatestRef(expanded);
  const toggleExpanded = useCallback(
    (id: Key) => {
      const current = expandedRef.current;
      setExpanded(current.includes(id) ? current.filter((key) => key !== id) : [...current, id]);
    },
    [expandedRef, setExpanded],
  );

  const handleSortChange = useCallback(
    ({ column: columnKey }: SortDescriptor) => {
      const column = String(columnKey);
      let nextDescriptor: ColumnSort | undefined = { column, direction: 'descending' };
      if (sortDescriptor?.column === column && sortDescriptor.direction === 'descending') {
        nextDescriptor = { column, direction: 'ascending' };
      } else if (
        !disallowEmptySort &&
        sortDescriptor?.column === column &&
        appliedSortRef.current?.column === column &&
        appliedSortRef.current.direction === 'ascending'
      ) {
        nextDescriptor = undefined;
      }
      appliedSortRef.current = nextDescriptor;
      setSortDescriptor(nextDescriptor);
    },
    [sortDescriptor, setSortDescriptor, disallowEmptySort],
  );

  const setColumnSort = useCallback(
    (columnId: string, direction?: 'ascending' | 'descending') => {
      const nextDescriptor = direction ? { column: columnId, direction } : undefined;
      appliedSortRef.current = nextDescriptor;
      setSortDescriptor(nextDescriptor);
    },
    [setSortDescriptor],
  );

  const emptyStateTitle = emptyState?.title;
  const emptyStateDescription = emptyState?.description;
  const emptyStateRenderer = useMemo(
    () =>
      isLoading
        ? renderLoadingState
        : (renderEmptyState ??
          (emptyStateTitle !== undefined && emptyStateDescription !== undefined
            ? () => <TableEmptyState title={emptyStateTitle} description={emptyStateDescription} />
            : undefined)),
    [emptyStateDescription, emptyStateTitle, isLoading, renderEmptyState],
  );
  const bodyRows = useMemo(() => (isLoading ? [] : flatRows), [isLoading, flatRows]);
  const hasFeedResultsRow = hasAdditionalFeedResults && !feedResultsInHeader;
  const showEmptyWithFeed = hasFeedResultsRow && bodyRows.length === 0 && emptyStateRenderer !== undefined;
  const feedItem = useMemo<FeedBodyItem>(
    () => ({ type: 'feed', id: FEED_RESULTS_ROW_ID, content: additionalFeedResults }),
    [additionalFeedResults],
  );
  const emptyItem = useMemo<EmptyBodyItem>(
    () => ({ type: 'empty', id: EMPTY_ROW_ID, render: emptyStateRenderer }),
    [emptyStateRenderer],
  );
  const [bodyItemCache] = useState(() => new BodyItemCache<T>());
  const hasLoadMoreTarget = hasMore && !isLoading && onLoadMore !== undefined;
  const bodyItems = useMemo(() => {
    const items: Array<BodyItem<T>> = [];

    if (hasFeedResultsRow) {
      items.push(feedItem);
    }

    for (const flatRow of bodyRows) {
      const isDetailExpanded = renderExpandedContent !== undefined && expandedSet.has(flatRow.id);
      items.push(
        bodyItemCache.row(
          flatRow,
          isDetailExpanded,
          hasLoadMoreTarget && flatRow.id === lastFlatRowId,
          activeRowId !== undefined && flatRow.id === activeRowId,
        ),
      );
      const canExpand = isRowExpandable ? isRowExpandable(flatRow.row) : renderExpandedContent !== undefined;
      if (canExpand && isDetailExpanded) {
        items.push(bodyItemCache.detail(flatRow.row, flatRow.id));
      }
    }

    if (showEmptyWithFeed) {
      items.push(emptyItem);
    }

    return items;
  }, [
    activeRowId,
    bodyItemCache,
    bodyRows,
    emptyItem,
    expandedSet,
    feedItem,
    hasFeedResultsRow,
    hasLoadMoreTarget,
    isRowExpandable,
    lastFlatRowId,
    renderExpandedContent,
    showEmptyWithFeed,
  ]);
  const columnCount = displayColumns.length + (hasSelectionColumn ? 1 : 0) + (hasFunctionColumn ? 1 : 0);
  const virtualHeadingHeight = virtualization?.headingHeight ?? virtualization?.estimatedHeadingHeight ?? 25;

  const getInlineEdge = (index: number): DataTableInlineEdge | undefined => {
    if (horizontalPadding === undefined) {
      return undefined;
    }
    const isStart = index === 0 && !hasSelectionColumn;
    const isEnd = index === displayColumns.length - 1 && !hasFunctionColumn;
    if (isStart && isEnd) {
      return 'both';
    }
    return isStart ? 'start' : isEnd ? 'end' : undefined;
  };

  const endPinStackOrder = (stickyEndIndex: number) => displayColumns.length + 2 + (endStickyCount - stickyEndIndex);

  const headerColumns = displayColumns.map((column, index) => {
    const stickyIndex =
      index < stickyDataColumnCount ? index + (hasSelectionColumn && hasStickyColumns ? 1 : 0) : undefined;
    const fromEnd = displayColumns.length - 1 - index;
    const stickyEndIndex = column.pin === 'end' ? fromEnd + (pinFunction ? 1 : 0) : undefined;
    const stickyEdge =
      stickyIndex !== undefined && index === lastStickyColumnIndex
        ? 'start'
        : stickyEndIndex !== undefined && stickyEndIndex === endStickyCount - 1
          ? 'end'
          : undefined;
    const moveLeft = orderAfterVisualMove(orderedColumns, unfoldedColumns, column.id, 'left');
    const moveRight = orderAfterVisualMove(orderedColumns, unfoldedColumns, column.id, 'right');
    const resizedWidth =
      defaultColumnActionsEnabled && resizedWidths && column.width === undefined
        ? (resizedWidths.get(column.id) ?? column.defaultWidth ?? '1fr')
        : undefined;

    return (
      <DataTableColumnHeader
        key={column.id}
        column={column}
        defaultColumnActionsEnabled={defaultColumnActionsEnabled}
        isResizable={isColumnResizable}
        widthOverride={columnWidths?.[column.id] ?? (controlsResizedWidths ? resizedWidth : undefined)}
        defaultWidthOverride={controlsResizedWidths ? undefined : resizedWidth}
        stickyIndex={stickyIndex}
        stickyOffset={stickyIndex === undefined ? undefined : stickyStartOffset(stickyIndex)}
        stickyEndIndex={stickyEndIndex}
        stickyEndOffset={
          scrollContainer === 'ancestor' || stickyEndIndex === undefined ? undefined : stickyEndOffset(stickyEndIndex)
        }
        stickyEdge={stickyEdge}
        inlineEdge={getInlineEdge(index)}
        isDragging={columnReordering.draggedColumn === column.id}
        dropPosition={
          columnReordering.dropTarget?.columnId === column.id ? columnReordering.dropTarget.position : undefined
        }
        onHeaderPointerDown={(event) =>
          columnReordering.onHeaderPointerDown(event, column.id, column.isReorderable !== false)
        }
        sortDescriptor={sortDescriptor}
        canHide={
          column.isHideable !== false &&
          (defaultColumnActionsEnabled
            ? hasColumnManager || onHiddenColumnsChange !== undefined
            : displayColumns.length > 1)
        }
        isHideDisabled={displayColumns.length <= 1}
        canRemoveSort={!disallowEmptySort}
        onSort={(direction) => setColumnSort(column.id, direction)}
        onHide={() => toggleColumnVisibility(column.id, false)}
        onResetSize={() => resetColumnSize(column)}
        canMoveLeft={moveLeft !== undefined}
        canMoveRight={moveRight !== undefined}
        onMoveLeft={() => {
          if (moveLeft) {
            applyColumnOrder(moveLeft);
          }
        }}
        onMoveRight={() => {
          if (moveRight) {
            applyColumnOrder(moveRight);
          }
        }}
        stackOrder={stickyEndIndex === undefined ? displayColumns.length - index + 1 : endPinStackOrder(stickyEndIndex)}
      />
    );
  });

  if (hasSelectionColumn) {
    const stickyIndex = hasStickyColumns ? 0 : undefined;
    headerColumns.unshift(
      <Column
        key={SELECTION_COLUMN_ID}
        id={SELECTION_COLUMN_ID}
        width={selectionColumnWidth}
        minWidth={selectionColumnWidth}
        maxWidth={selectionColumnWidth}
        textValue={msg('ui.dataTable.selection.columnLabel')}
        className={clsx(styles.utilityColumn, styles.selectionColumn, {
          [styles.stickyColumn]: stickyIndex !== undefined,
        })}
        data-sticky-column-index={stickyIndex}
        data-inline-edge={selectionEdge}
        style={{
          zIndex: stickyIndex === undefined ? undefined : displayColumns.length + 2,
          insetInlineStart: stickyIndex === undefined ? undefined : stickyStartOffset(stickyIndex),
        }}
      >
        <div className={styles.utilityContent}>
          {selectionMode === 'multiple' ? (
            <Checkbox slot="selection" />
          ) : (
            <span className={styles.visuallyHidden}>
              <Message id="ui.dataTable.selection.columnLabel" />
            </span>
          )}
        </div>
      </Column>,
    );
  }

  if (hasFunctionColumn) {
    headerColumns.push(
      <Column
        key={FUNCTION_COLUMN_ID}
        id={FUNCTION_COLUMN_ID}
        width={functionColumnWidth}
        minWidth={functionColumnWidth}
        maxWidth={functionColumnWidth}
        textValue={
          hasColumnManager
            ? msg('ui.dataTable.functions.manageColumnsColumnLabel')
            : msg('ui.dataTable.functions.rowActionsColumnLabel')
        }
        className={clsx(styles.utilityColumn, styles.functionColumn, {
          [styles.stickyColumn]: pinFunction,
        })}
        data-sticky-end-index={pinFunction ? 0 : undefined}
        data-sticky-column-edge={pinFunction && endStickyCount === 1 ? 'end' : undefined}
        data-inline-edge={functionEdge}
        style={{
          zIndex: pinFunction ? endPinStackOrder(0) : undefined,
          insetInlineEnd: scrollContainer === 'ancestor' || !pinFunction ? undefined : stickyEndOffset(0),
        }}
      >
        <div className={styles.utilityContent}>
          {hasColumnManager ? (
            <ColumnManager
              columns={orderedColumns}
              hiddenColumns={hiddenColumnIds}
              attributeColumns={attributeColumns}
              attributeColumnKeys={selectedAttributeKeys}
              onToggleVisibility={toggleColumnVisibility}
              onAddAttributeColumn={addAttributeColumn}
              onRemoveAttributeColumn={removeAttributeColumn}
            />
          ) : (
            <span className={styles.visuallyHidden}>
              <Message id="ui.dataTable.functions.rowActionsColumnLabel" />
            </span>
          )}
        </div>
      </Column>,
    );
  }

  const table = (
    <Table
      {...props}
      ref={setTableElement}
      className={clsx(styles.table, styles[size], {
        [styles.bordered]: bordered,
        [styles.headerTopBorder]: hasHeaderTopBorder,
        [styles.inlinePadded]: horizontalPadding !== undefined,
        [styles.stickyHeader]: isHeaderSticky || virtualization,
        [styles.virtualizedTable]: virtualization,
      })}
      style={
        virtualization
          ? {
              scrollPaddingTop: Math.max(virtualHeadingHeight, measuredHeaderHeight) + bannerHeight,
            }
          : undefined
      }
      sortDescriptor={sortDescriptor}
      onSortChange={handleSortChange}
      selectionMode={selectionMode}
      selectedKeys={selection}
      onSelectionChange={handleSelectionChange}
      onRowAction={onRowAction ? handleRowAction : undefined}
      aria-busy={isLoading || isLoadingMore || undefined}
    >
      <TableHeader ref={headerRef} className={styles.tableHeader}>
        {headerColumns}
      </TableHeader>
      <TableBody
        items={bodyItems}
        renderEmptyState={showEmptyWithFeed ? undefined : emptyStateRenderer}
        dependencies={[
          displayColumns,
          columnsById,
          hasFunctionColumn,
          hasSelectionColumn,
          isTree,
          narrow,
          foldedLines,
          stickyDataColumnCount,
          endStickyCount,
          pinFunction,
          scrollContainer,
          isVirtualized,
          getRowActions,
          getRowLabel,
          getRowHref,
          hasRowActivation,
          hasRowHover,
          hasSelectedRowHighlight,
          resolveCellActions,
          renderExpandedContent,
          columnCount,
          horizontalPadding,
        ]}
      >
        {(item: BodyItem<T>) => {
          if (item.type === 'feed') {
            return (
              <Row id={item.id} className={clsx(styles.row, styles.feedResultsRow)} data-additional-feed-results>
                <Cell colSpan={columnCount} className={clsx(styles.cell, styles.feedResultsCell)}>
                  {item.content}
                </Cell>
              </Row>
            );
          }

          if (item.type === 'empty') {
            return (
              <Row id={item.id} className={styles.row}>
                <Cell colSpan={columnCount} className={styles.cell}>
                  {item.render?.()}
                </Cell>
              </Row>
            );
          }

          if (item.type === 'detail') {
            return (
              <Row id={item.id} className={clsx(styles.row, styles.expandedRow)}>
                <Cell colSpan={columnCount} className={clsx(styles.cell, styles.expandedCell)}>
                  {renderExpandedContent?.(item.row)}
                </Cell>
              </Row>
            );
          }

          const flatRow = item.flatRow;
          const primaries = displayColumns.map((column) => column.renderCell(flatRow.row));
          const rowLabel = getRowLabel?.(flatRow.row) ?? getPrimitiveLabel(primaries[0], flatRow.id);
          const cells = displayColumns.map((column, index) => {
            const showFoldedStack = narrow && foldedLines.length > 0 && column.id === displayColumns[0]?.id;
            const hasGutter = isTree && index === 0;
            const isMonotype = column.contentStyle === 'monotype';
            const isSparkline = column.contentStyle === 'sparkline';
            const primary = primaries[index];
            const secondary = column.renderSecondaryCell?.(flatRow.row);
            const cellActions = resolveCellActions(
              column,
              flatRow.row,
              primary,
              showFoldedStack,
              rowCellActionHandlers,
            );
            const cellButtons = column.getCellButtons?.(flatRow.row) ?? [];
            const onCellAction = column.onCellAction;
            const hasKeyboardCellAction = onCellAction !== undefined && cellActions.length === 0;
            const opensRow = column.isRowHeader === true && hasRowActivation;
            const stickyIndex =
              index < stickyDataColumnCount ? index + (hasSelectionColumn && hasStickyColumns ? 1 : 0) : undefined;
            const fromEnd = displayColumns.length - 1 - index;
            const stickyEndIndex = column.pin === 'end' ? fromEnd + (pinFunction ? 1 : 0) : undefined;
            const stickyEdge =
              stickyIndex !== undefined && index === lastStickyColumnIndex
                ? 'start'
                : stickyEndIndex !== undefined && stickyEndIndex === endStickyCount - 1
                  ? 'end'
                  : undefined;

            return (
              <Cell
                key={column.id}
                className={clsx(styles.cell, {
                  [styles.cellNumeric]: column.align === 'end',
                  [styles.cellWithGutter]: hasGutter,
                  [styles.cellWithActions]: cellActions.length > 0,
                  [styles.cellInteractive]:
                    cellActions.length > 0 || cellButtons.length > 0 || opensRow || onCellAction !== undefined,
                  [styles.cellSparkline]: isSparkline,
                  [styles.cellWrapped]: column.wrapsContent,
                  [styles.actionableCell]: onCellAction !== undefined,
                  [styles.stickyColumn]: stickyIndex !== undefined || stickyEndIndex !== undefined,
                })}
                data-sticky-column-index={stickyIndex}
                data-sticky-end-index={stickyEndIndex}
                data-sticky-column-edge={stickyEdge}
                data-inline-edge={getInlineEdge(index)}
                data-cell-action-column={hasKeyboardCellAction ? column.id : undefined}
                data-cell-action-row={hasKeyboardCellAction ? String(flatRow.id) : undefined}
                style={{
                  insetInlineStart: stickyIndex === undefined ? undefined : stickyStartOffset(stickyIndex),
                  insetInlineEnd:
                    scrollContainer === 'ancestor' || stickyEndIndex === undefined
                      ? undefined
                      : stickyEndOffset(stickyEndIndex),
                }}
                onClick={
                  cellActions.length > 0
                    ? openActionsFromCell(flatRow.id, column.id)
                    : onCellAction
                      ? (event) => {
                          if (isInteractiveTarget(event.target)) {
                            return;
                          }
                          event.stopPropagation();
                          blurFocusWithin(event.currentTarget);
                          onCellAction(flatRow.row, column.ref);
                        }
                      : undefined
                }
                data-cell-actions={cellActions.length > 0 || undefined}
              >
                <div className={styles.cellContent}>
                  {hasGutter && (
                    <DataTableTreeGutter
                      level={flatRow.level}
                      ancestorLines={flatRow.ancestorLines}
                      hasChildren={flatRow.hasChildren}
                      isExpanded={flatRow.isExpanded}
                      isLastChild={flatRow.isLastChild}
                      label={rowLabel}
                      onToggle={() => {
                        toggleExpanded(flatRow.id);
                      }}
                    />
                  )}
                  <div className={clsx(styles.cellText, { [styles.foldedStack]: showFoldedStack })}>
                    <div
                      className={clsx(styles.cellPrimary, {
                        [styles.foldedNameRow]: showFoldedStack,
                        [styles.cellStrong]: column.isRowHeader,
                        [styles.cellMonotype]: isMonotype,
                        [styles.cellEmpty]: isBlank(primary),
                      })}
                      data-cell-primary=""
                    >
                      {isBlank(primary) ? msg('ui.dataTable.cell.empty') : primary}
                    </div>
                    {showFoldedStack &&
                      foldedLines.map((line) => {
                        const parts = line.flatMap((id) => {
                          const value = columnsById.get(id)?.renderCell(flatRow.row);
                          return isBlank(value) ? [] : [{ id, value }];
                        });
                        if (parts.length === 0) {
                          return null;
                        }
                        if (parts.length === 1) {
                          return (
                            <div key={parts[0].id} className={styles.foldedLine}>
                              {parts[0].value}
                            </div>
                          );
                        }
                        return (
                          <div key={parts.map((part) => part.id).join(':')} className={styles.foldedMetaLine}>
                            {parts.map((part) => (
                              <span key={part.id} className={styles.foldedMetaItem}>
                                {part.value}
                              </span>
                            ))}
                          </div>
                        );
                      })}
                    {column.renderSecondaryCell && (
                      <div
                        className={clsx(styles.cellSecondary, {
                          [styles.cellMonotype]: isMonotype,
                          [styles.cellEmpty]: isBlank(secondary),
                        })}
                      >
                        {isBlank(secondary) ? msg('ui.dataTable.cell.empty') : secondary}
                      </div>
                    )}
                  </div>
                  {cellButtons.length > 0 && (
                    <DataTableCellButtons buttons={cellButtons} row={flatRow.row} rowLabel={rowLabel} />
                  )}
                </div>
              </Cell>
            );
          });

          if (hasSelectionColumn) {
            const stickyIndex = hasStickyColumns ? 0 : undefined;
            cells.unshift(
              <Cell
                key={SELECTION_COLUMN_ID}
                className={clsx(styles.cell, styles.utilityCell, styles.selectionColumn, {
                  [styles.stickyColumn]: stickyIndex !== undefined,
                })}
                data-sticky-column-index={stickyIndex}
                data-inline-edge={selectionEdge}
                style={{ insetInlineStart: stickyIndex === undefined ? undefined : stickyStartOffset(stickyIndex) }}
              >
                <div className={styles.utilityContent}>
                  <Checkbox slot="selection" />
                </div>
              </Cell>,
            );
          }

          if (hasFunctionColumn) {
            const rowActions = getRowActions?.(flatRow.row) ?? [];
            cells.push(
              <Cell
                key={FUNCTION_COLUMN_ID}
                className={clsx(styles.cell, styles.utilityCell, styles.functionColumn, styles.functionCell, {
                  [styles.cellWithActions]: rowActions.length > 0,
                  [styles.stickyColumn]: pinFunction,
                })}
                data-sticky-end-index={pinFunction ? 0 : undefined}
                data-sticky-column-edge={pinFunction && endStickyCount === 1 ? 'end' : undefined}
                data-inline-edge={functionEdge}
                style={{
                  insetInlineEnd: scrollContainer === 'ancestor' || !pinFunction ? undefined : stickyEndOffset(0),
                }}
                onClick={rowActions.length > 0 ? openActionsFromCell(flatRow.id, FUNCTION_COLUMN_ID) : undefined}
              >
                <div className={styles.utilityContent}>
                  <DataTableRowActions
                    actions={rowActions}
                    row={flatRow.row}
                    rowId={flatRow.id}
                    rowLabel={rowLabel}
                    columnId={FUNCTION_COLUMN_ID}
                  />
                </div>
              </Cell>,
            );
          }

          const loadMoreRef = item.isLoadMoreTarget ? loadMoreTargetRef : undefined;

          return (
            <Row
              ref={item.isActive ? currentRowRef(loadMoreRef) : loadMoreRef}
              id={flatRow.id}
              href={getRowHref?.(flatRow.row)}
              className={clsx(styles.row, {
                [styles.actionableRow]: hasRowActivation,
                [styles.selectedRowHighlight]: hasSelectedRowHighlight,
                [styles.expandedParentRow]: item.isDetailExpanded,
                [styles.activeRow]: item.isActive,
              })}
              data-active={item.isActive || undefined}
              data-level={flatRow.level + 1}
              style={isVirtualized ? { width: 'inherit', height: 'inherit' } : undefined}
              onMouseEnter={hasRowHover ? () => onRowHoverRef.current?.(flatRow.row) : undefined}
              onMouseLeave={hasRowHover ? () => onRowHoverRef.current?.(null) : undefined}
            >
              {cells}
            </Row>
          );
        }}
      </TableBody>
    </Table>
  );

  const virtualLayoutOptions: DataTableLayoutOptions = {
    ...virtualization,
    additionalResultsHeight: bannerHeight,
    renderedHeaderHeight: measuredHeaderHeight,
    stickyColumnCount: hasStickyColumns ? stickyDataColumnCount + (hasSelectionColumn ? 1 : 0) : 0,
    rendersAllColumns: scrollContainer === 'ancestor',
    stickyHeaderOffset: scrollContainer === 'ancestor' ? (stickyHeaderOffset ?? 0) : 0,
    scrollAnchorContent,
    scrollAnchorTarget,
  };
  const containerStyle: DataTableContainerStyle = {
    ...(height === undefined ? {} : { height, maxHeight: height }),
    ...(stickyHeaderOffset === undefined ? {} : { '--lp-data-table-sticky-top': `${stickyHeaderOffset}px` }),
    ...(headerRowHeight > 0 ? { '--lp-data-table-header-height': `${headerRowHeight}px` } : {}),
    ...(horizontalPadding === undefined
      ? {}
      : { '--lp-data-table-inline-padding': horizontalPaddingProperty(horizontalPadding) }),
    scrollPaddingInlineStart: stickyStartExtent,
    scrollPaddingInlineEnd: stickyEndExtent,
  };

  const tableView = virtualization ? (
    <Virtualizer
      layout={virtualLayout}
      layoutOptions={virtualLayoutOptions}
      shouldObserveItemSize={renderExpandedContent !== undefined}
    >
      {table}
    </Virtualizer>
  ) : (
    table
  );

  const openCellActionsColumn =
    openCellActions && openCellActions.columnId !== FUNCTION_COLUMN_ID
      ? columnsById.get(openCellActions.columnId)
      : undefined;
  const openCellActionsRow = openCellActions ? rowById.get(openCellActions.rowId) : undefined;
  const cellActionsMenu =
    openCellActions && openCellActionsColumn && openCellActionsRow !== undefined ? (
      <DataTableCellActions
        actions={resolveCellActions(
          openCellActionsColumn,
          openCellActionsRow,
          openCellActionsColumn.renderCell(openCellActionsRow),
          narrow && foldedLines.length > 0 && openCellActionsColumn.id === displayColumns[0]?.id,
          { getDisplayedText: readOpenCellText, onCellFilter },
        )}
        columnName={openCellActionsColumn.name}
        row={openCellActionsRow}
        rowLabel={
          getRowLabel?.(openCellActionsRow) ??
          getPrimitiveLabel(displayColumns[0]?.renderCell(openCellActionsRow), openCellActions.rowId)
        }
        triggerRef={openCellTriggerRef}
        isOpen
        onOpenChange={(isOpen) => {
          if (isOpen) {
            return;
          }
          const active = document.activeElement;
          shouldRestoreCellFocusRef.current = active === document.body || Boolean(active?.closest('[role="menu"]'));
          setOpenCellActions(undefined);
        }}
      />
    ) : undefined;

  return (
    <DataTableFoldedColumnsContext.Provider value={foldedColumnRefs}>
      <div
        ref={setContainerElement}
        className={clsx(styles.container, {
          [styles.ancestorScroll]: scrollContainer === 'ancestor',
          [styles.bordered]: bordered,
        })}
        style={containerStyle}
        onKeyDownCapture={handleContainerKeyDownCapture}
        onPointerDownCapture={handleContainerPointerDownCapture}
        data-overflow-start={scrollEdges.start ? 'true' : undefined}
        data-overflow-end={scrollEdges.end ? 'true' : undefined}
        aria-busy={isLoading || isLoadingMore || undefined}
      >
        <ResizableTableContainer
          className={clsx({ [styles.virtualizedColumnSizer]: virtualization })}
          onResize={defaultColumnActionsEnabled ? handleColumnResize : emitColumnResize}
          onResizeEnd={handleColumnResizeEnd}
        >
          <DataTableOpenCellActionsContext.Provider value={openCellActionsState}>
            {scrollContainer === 'ancestor' ? (
              <>
                <div ref={setAncestorXScroll} className={styles.ancestorXScroll}>
                  {tableView}
                </div>
                <div
                  ref={setAncestorXBar}
                  className={styles.ancestorXBar}
                  hidden={ancestorXBarHidden}
                  data-horizontal-scrollbar=""
                >
                  <div className={styles.ancestorXBarInner} style={{ width: ancestorXContentWidth }} />
                </div>
              </>
            ) : (
              tableView
            )}
          </DataTableOpenCellActionsContext.Provider>
        </ResizableTableContainer>
        {cellActionsMenu}
        {additionalResults && (
          <DataTableAdditionalResultsRow
            additionalResults={additionalResults}
            columnCount={headerColumns.length}
            headerElement={headerElement}
            isSticky={isHeaderSticky || Boolean(virtualization)}
            onHeightChange={reportAdditionalResultsHeight}
          />
        )}
        {feedResultsInHeader && (
          <DataTableHeaderBannerRow
            variant="additionalFeedResults"
            columnCount={headerColumns.length}
            headerElement={headerElement}
            isSticky
            onHeightChange={reportAdditionalResultsHeight}
          >
            {additionalFeedResults}
          </DataTableHeaderBannerRow>
        )}
        {isLoadingMore && (
          <div className={styles.loadMoreStatus} aria-busy="true">
            <ProgressBar aria-label={msg('ui.dataTable.loadMore.loadingLabel')} isIndeterminate size="small" />
          </div>
        )}
      </div>
    </DataTableFoldedColumnsContext.Provider>
  );
}
